/**
 * CameraHealthCheckDebugService
 * 
 * Debugovacie nástroje pre vizualizáciu kontroly zdravia kamery.
 * Samostatná služba oddelená od produkčnej logiky.
 * 
 * Poskytuje:
 *   1. Živé zobrazenie video streamu z kamery (zmenšené)
 *   2. Vizualizáciu vzorkovaných pixelov s priestorovým mapovaním
 *   3. Simuláciu zamrznutia kamery pre testovanie
 * 
 * Použitie:
 *   - Zapína sa cez config.EnableCameraHealthCheckDebugMode
 *   - Zobrazuje canvasy a video na obrazovke s fixnou pozíciou
 *   - Pomáha pri ladení detekčných algoritmov
 */
angular.module('screensApp')
    .service('CameraHealthCheckDebugService', function() {

        var self = this;

        /**
         * Zobrazí živý video stream z kamery v debug režime
         * Vytvorí video element, ktorý zdieľa rovnaký MediaStream ako originálny element
         * 
         * @param {HTMLVideoElement} videoElement - Originálny video element s kamerovým streamom
         * @param {string} videoId - ID pre debug video element
         * @param {Object} position - Objekt s CSS pozičnými vlastnosťami {top, left, right, bottom, scale, transformOrigin}
         * @returns {HTMLVideoElement} Debug video element
         * 
         * Použitie:
         *   - Zobrazuje živý stream zmenšený na 40% v ľavom hornom rohu
         *   - Umožňuje vizuálne potvrdiť, že sa monitoruje správny stream
         *   - Modrý okraj odlišuje od červených sample canvasov
         *   - Zobrazuje stav MediaStreamTrack (enabled/disabled)
         */
        self.showVideoCanvas = function(videoElement, videoId, position) {
            position = position || { top: "0", left: "0", scale: "scale(.4)", transformOrigin: "top left" };
            
            // Vytvor alebo získaj existujúci video element pre debug
            let debugVideo = document.getElementById(videoId);
            
            if (!debugVideo) {
                debugVideo = document.createElement('video');
                debugVideo.id = videoId;
                debugVideo.autoplay = true;
                debugVideo.muted = true;
                debugVideo.playsInline = true;
                document.body.appendChild(debugVideo);
            }
            
            // Priradí rovnaký stream ako má originálny video element
            if (videoElement.srcObject && debugVideo.srcObject !== videoElement.srcObject) {
                debugVideo.srcObject = videoElement.srcObject;
            }
            
            // Nastav štúly
            debugVideo.style.display = "block";
            debugVideo.style.position = "fixed";
            debugVideo.style.top = position.top || "auto";
            debugVideo.style.left = position.left || "auto";
            debugVideo.style.right = position.right || "auto";
            debugVideo.style.bottom = position.bottom || "auto";
            debugVideo.style.zIndex = "10000";
            debugVideo.style.pointerEvents = "none";
            debugVideo.style.transform = position.scale || "scale(.4)";
            debugVideo.style.transformOrigin = position.transformOrigin || "top left";
            debugVideo.style.boxShadow = "0 0 5px blue";
            
            // Získaj MediaStreamTrack stav
            let trackEnabled = true;
            let trackMuted = false;
            let trackReadyState = 'unknown';
            if (videoElement.srcObject) {
                const tracks = videoElement.srcObject.getVideoTracks();
                if (tracks.length > 0) {
                    trackEnabled = tracks[0].enabled;
                    trackMuted = tracks[0].muted;
                    trackReadyState = tracks[0].readyState;
                }
            }
            
            // Vytvor alebo aktualizuj label pre video
            let videoLabel = document.getElementById(videoId + '-label');
            if (!videoLabel) {
                videoLabel = document.createElement('div');
                videoLabel.id = videoId + '-label';
                document.body.appendChild(videoLabel);
            }
            
            // Nastav farbu labelu podľa track stavu
            // Červená: disabled alebo muted (technický problém)
            // Oranžová: enabled ale muted (obraz zamrzol)
            // Modrá: enabled a nie muted (OK)
            let labelBgColor = "rgba(0, 0, 255, 0.8)";
            if (!trackEnabled) {
                labelBgColor = "rgba(255, 0, 0, 0.8)"; // Červená - disabled
            } else if (trackMuted) {
                labelBgColor = "rgba(255, 165, 0, 0.8)"; // Oranžová - muted
            }
            
            const trackStatusIcon = trackEnabled ? "✓" : "✗";
            const trackStatusText = trackEnabled ? "enabled" : "DISABLED";
            const mutedStatusIcon = trackMuted ? "🔇" : "🔊";
            const mutedStatusText = trackMuted ? "MUTED" : "ok";
            
            // Nastav štýy a text labelu
            videoLabel.style.position = "fixed";
            videoLabel.style.top = position.top || "0";
            videoLabel.style.left = position.left || "0";
            videoLabel.style.transform = "scale(1)";
            videoLabel.style.transformOrigin = position.transformOrigin || "top left";
            videoLabel.style.zIndex = "10002";
            videoLabel.style.backgroundColor = labelBgColor;
            videoLabel.style.color = "white";
            videoLabel.style.padding = "5px 10px";
            videoLabel.style.fontFamily = "monospace";
            videoLabel.style.fontSize = "14px";
            videoLabel.style.fontWeight = "bold";
            videoLabel.style.pointerEvents = "none";
            videoLabel.textContent = "📹 Stream | " + trackStatusIcon + " " + trackStatusText + " | " + mutedStatusIcon + " " + mutedStatusText + " (" + trackReadyState + ")";
            
            return debugVideo;
        };

        /**
         * Zobrazí vzorkované pixely ako miniatúrny obraz so zachovaním priestorových pozícií
         * 
         * @param {Uint8ClampedArray} imageData - RGBA pixelové dáta z canvas.getImageData()
         * @param {number} originalWidth - Pôvodná šírka video snímku
         * @param {number} originalHeight - Pôvodná výška video snímku
         * @param {number} sampleThreshold - Vzorkovací prah (každý N-tý pixel)
         * @param {string} canvasId - ID pre debug canvas element
         * @param {Object} position - Objekt s CSS pozičnými vlastnosťami {top, left, right, bottom}
         * 
         * Algoritmus:
         *   1. Vypočíta rozmer vzorkovaného obrazu (width/N x height/N)
         *   2. Pre každý vzorkovaný pixel vypočíta jeho pôvodnú pozíciu (origX, origY)
         *   3. Namapuje pixel na novú pozíciu v sample canvase (sampledX, sampledY)
         *   4. Vykreslí pixel s RGB hodnotami z pôvodných dát
         *   5. Zväčší canvas pomocou CSS pre lepšiu viditeľnosť (pixelated rendering)
         * 
         * Použitie:
         *   - Dva canvasy: jeden pre black check (krok 10), druhý pre frozen check (krok 50)
         *   - Červený okraj odlišuje od modrého živého videa
         *   - imageRendering: pixelated pre ostrý vzhľad
         */
        self.showSampledCanvas = function(imageData, originalWidth, originalHeight, sampleThreshold, canvasId, position) {
            canvasId = canvasId || 'camera-health-check-signature-canvas';
            position = position || { top: "0", left: "0" };
            
            const debugCanvas = document.getElementById(canvasId) || document.createElement('canvas');
            debugCanvas.id = canvasId;
            
            // Vypočítaj rozmery vzorkovaného obrazu (každý N-tý pixel)
            const sampledWidth = Math.ceil(originalWidth / sampleThreshold);
            const sampledHeight = Math.ceil(originalHeight / sampleThreshold);
            
            debugCanvas.width = sampledWidth;
            debugCanvas.height = sampledHeight;
            
            // Zväčši na obrazovke pre lepšiu viditeľnosť
            const scale = Math.min(8, 300 / Math.max(sampledWidth, sampledHeight)); // Dynamický scale
            debugCanvas.style.display = "block";
            debugCanvas.style.position = "fixed";
            debugCanvas.style.top = position.top;
            debugCanvas.style.left = position.left;
            debugCanvas.style.bottom = position.bottom || "auto";
            debugCanvas.style.right = position.right || "auto";
            debugCanvas.style.width = (sampledWidth * scale) + "px";
            debugCanvas.style.height = (sampledHeight * scale) + "px";
            debugCanvas.style.zIndex = "10001";
            debugCanvas.style.pointerEvents = "none";
            debugCanvas.style.boxShadow = "0 0 5px red";
            debugCanvas.style.imageRendering = "pixelated"; // Ostrý pixelový vzhľad
            
            if (!document.body.contains(debugCanvas)) {
                document.body.appendChild(debugCanvas);
            }

            // Vytvor alebo aktualizuj label pre canvas
            let canvasLabel = document.getElementById(canvasId + '-label');
            if (!canvasLabel) {
                canvasLabel = document.createElement('div');
                canvasLabel.id = canvasId + '-label';
                document.body.appendChild(canvasLabel);
            }
            
            // Urč typ kontroly podľa canvasId
            let labelText = '';
            if (canvasId.includes('black')) {
                labelText = '⚫ Čierne pixely';
            } else if (canvasId.includes('frozen')) {
                labelText = '🧊 Frozen detekcia';
            } else {
                labelText = '📊 Vzorkované pixely';
            }
            
            // Nastav štýy a text labelu
            canvasLabel.style.position = "fixed";
            canvasLabel.style.top = position.top || "auto";
            canvasLabel.style.left = position.left || "auto";
            canvasLabel.style.bottom = position.bottom || "auto";
            canvasLabel.style.right = position.right || "auto";
            canvasLabel.style.zIndex = "10002";
            canvasLabel.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
            canvasLabel.style.color = "white";
            canvasLabel.style.padding = "5px 10px";
            canvasLabel.style.fontFamily = "monospace";
            canvasLabel.style.fontSize = "12px";
            canvasLabel.style.fontWeight = "bold";
            canvasLabel.style.pointerEvents = "none";
            canvasLabel.textContent = labelText;

            const debugCtx = debugCanvas.getContext('2d', { willReadFrequently: true });
            debugCtx.imageSmoothingEnabled = false;
            
            // Vyčisti canvas
            debugCtx.fillStyle = 'black';
            debugCtx.fillRect(0, 0, debugCanvas.width, debugCanvas.height);
            
            // Vykresli vzorkované pixely na ich priestorových pozíciách
            // Preskakujeme po (4 * sampleThreshold) pretože imageData je [R,G,B,A, R,G,B,A, ...]
            for (let i = 0; i < imageData.length; i += 4 * sampleThreshold) {
                // Vypočítaj pôvodnú pozíciu pixela vo videu
                const pixelIndex = i / 4; // Index pixela (nie RGBA hodnoty)
                const origX = pixelIndex % originalWidth; // X súradnica v pôvodnom snímku
                const origY = Math.floor(pixelIndex / originalWidth); // Y súradnica v pôvodnom snímku
                
                // Vypočítaj pozíciu v zmenšenom sample canvase
                const sampledX = Math.floor(origX / sampleThreshold); // Nová X pozícia
                const sampledY = Math.floor(origY / sampleThreshold); // Nová Y pozícia
                
                if (sampledX < sampledWidth && sampledY < sampledHeight) {
                    const r = imageData[i];
                    const g = canvasId == "camera-health-check-frozen-canvas" ? 0 : ( canvasId == "camera-health-check-black-canvas" ? imageData[i] : imageData[i + 1] || 0 );
                    const b = canvasId == "camera-health-check-frozen-canvas" ? 0 : ( canvasId == "camera-health-check-black-canvas" ? imageData[i] : imageData[i + 2] || 0 );
                    
                    debugCtx.fillStyle = `rgb(${r},${g},${b})`;
                    debugCtx.fillRect(sampledX, sampledY, 1, 1);
                }
            }
        };

        // ============================================
        // SIMULÁCIA REÁLNYCH CHYBOVÝCH STAVOV
        // ============================================
        // Podľa dokumentácie v chyby-streamu-z-kamery.js
        
        /**
         * PRÍPAD 1: Simulácia zamrznutého streamu (muted=true)
         * Reálna chyba: kamera zamrzla, obraz zostal
         * 
         * @param {string|HTMLVideoElement} videoSelector - CSS selector alebo video element
         * @param {number} delaySeconds - Oneskorenie pred simuláciou (default: 2)
         * 
         * Simuluje stav:
         *   contentHint: ""
         *   enabled: true
         *   muted: true  ← PROBLÉM
         *   readyState: "live"
         * 
         * Poznámka: Technicky nie je možné programovo nastaviť muted=true
         * (je to read-only property riadená hardvérom). Táto metóda použije
         * workaround cez zamrznutý canvas stream.
         * 
         * Alternatívne metódy:
         *   A) track.stop() → readyState='ended' (nie presná simulácia)
         *   B) Canvas stream bez refresh → zamrznutý obraz (lepšia simulácia)
         *   C) OBS Virtual Camera - pause/unpause (manuálne testovanie)
         */
        self.simulateMutedStream = function(videoSelector, delaySeconds) {
            delaySeconds = delaySeconds || 0;
            console.warn('🔇 DEBUG: Simulujem MUTED stream (kamera zamrznutá) za ' + delaySeconds + ' sekúnd...');
            console.warn('🔇 DEBUG: Metóda: Nahradím stream zamrznutým canvas streamom (simuluje Camera freeze)');
            console.warn('🔇 DEBUG: Selector:', videoSelector);

            const isVideoReady = () => {
                // Dynamicky hľadaj element pri každej kontrole
                const videoElement = typeof videoSelector === 'string' 
                    ? document.querySelector(videoSelector)
                    : videoSelector;
                    
                if (!videoElement) {
                    console.log('🔍 DEBUG: videoElement neexistuje (selector:', videoSelector, ')');
                    return false;
                }
                if (!videoElement.srcObject) {
                    console.log('🔍 DEBUG: srcObject neexistuje');
                    return false;
                }
                const tracks = videoElement.srcObject.getVideoTracks();
                if (tracks.length === 0) {
                    console.log('🔍 DEBUG: žiadne video tracks');
                    return false;
                }
                if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                    console.log('🔍 DEBUG: videoWidth/Height je 0:', videoElement.videoWidth, videoElement.videoHeight);
                    return false;
                }
                console.log('🔍 DEBUG: Video je pripravený!');
                return videoElement;
            };
            
            const executeSimulation = () => {
                const videoElement = typeof videoSelector === 'string'
                    ? document.querySelector(videoSelector)
                    : videoSelector;
                    
                if (videoElement && videoElement.srcObject) {
                    const track = videoElement.srcObject.getVideoTracks()[0];
                    if (track) {
                        console.warn('🔇 DEBUG: Pred:', {
                            enabled: track.enabled,
                            muted: track.muted,
                            readyState: track.readyState
                        });
                        
                        // Vytvor canvas so zamrznutým snímkom
                        const canvas = document.createElement('canvas');
                        canvas.width = videoElement.videoWidth;
                        canvas.height = videoElement.videoHeight;
                        const ctx = canvas.getContext('2d');
                        
                        // Nakresli aktuálny frame (obraz zamrzne na tomto snímku)
                        ctx.drawImage(videoElement, 0, 0);
                        
                        // Vytvor nový stream z canvasu (0 FPS = zamrznutý obraz)
                        // Toto simuluje kameru, ktorá prestala streamovať nové snímky
                        const frozenStream = canvas.captureStream(0); // 0 FPS = žiadne nové snímky
                        
                        // Uložíme si pôvodný stream pre možné obnovenie
                        videoElement._originalStream = videoElement.srcObject;
                        
                        // Nahraď originálny stream zamrznutým streamom
                        videoElement.srcObject = frozenStream;
                        
                        console.warn('🔇 DEBUG: Po:', {
                            enabled: frozenStream.getVideoTracks()[0].enabled,
                            muted: frozenStream.getVideoTracks()[0].muted,
                            readyState: frozenStream.getVideoTracks()[0].readyState
                        });
                        console.warn('🔇 DEBUG: Stream nahradený zamrznutým canvas streamom (0 FPS)!');
                        console.warn('🔇 DEBUG: Toto simuluje kameru, ktorá prestala poskytovať nové snímky');
                        console.warn('🔇 DEBUG: Monitoring by mal detekovať zamrznutý obraz (rovnaká frame signatúra)');
                        console.warn('🔇 DEBUG: Pre obnovenie: videoElement.srcObject = videoElement._originalStream');
                        
                        // Pause debug video ak existuje
                        const debugVideo = document.getElementById('camera-health-check-video-debug');
                        if (debugVideo) {
                            debugVideo.pause();
                            console.warn('🔇 DEBUG: Debug video pausnuté (simuluje zamrznutie)');
                        }
                    }
                } else {
                    console.error('👾 DEBUG: Video element nemá srcObject');
                }
            };
            
            // Nastavíme MutationObserver
            console.log('⏳ DEBUG: Nastavujem MutationObserver na document.body...');
            
            const observer = new MutationObserver((mutations) => {
                console.log('🔔 DEBUG: MutationObserver zachytil zmenu v DOM, kontrolujem video...');
                const videoElement = isVideoReady();
                if (videoElement) {
                    observer.disconnect();
                    console.log('✅ DEBUG: Video pripravený, spúšťam simuláciu...');
                    setTimeout(executeSimulation, delaySeconds * 1000);
                }
            });
            
            // Sleduj document.body, lebo video element ešte neexistuje
            observer.observe(document.body, {
                attributes: true,
                subtree: true,
                childList: true
            });
            
            // Kontrola aktuálneho stavu
            console.log('🔍 DEBUG: Počiatočná kontrola stavu videa...');
            const videoElement = isVideoReady();
            if (videoElement) {
                observer.disconnect();
                console.log('✅ DEBUG: Video element už je pripravený!');
                setTimeout(executeSimulation, delaySeconds * 1000);
            } else {
                console.log('⏳ DEBUG: Video ešte nie je pripravené, čakám na MutationObserver...');
            }
        };
        
        /**
         * PRÍPAD 2: Simulácia ukončeného streamu (readyState="ended")
         * Reálna chyba: čierna obrazovka po reload, kamera nedostupná
         * 
         * @param {string|HTMLVideoElement} videoSelector - CSS selector alebo video element
         * @param {number} delaySeconds - Oneskorenie pred simuláciou (default: 2)
         * 
         * Simuluje stav:
         *   contentHint: ""
         *   enabled: true
         *   muted: false
         *   readyState: "ended"  ← PROBLÉM
         * 
         * Mechanizmus:
         *   - Zavolá track.stop() → nastaví readyState na "ended"
         *   - Monitoring by mal okamžite detekovať pri počiatočnej kontrole
         */
        self.simulateEndedStream = function(videoSelector, delaySeconds) {
            delaySeconds = delaySeconds || 0;
            console.warn('⛔ DEBUG: Simulujem ENDED stream (kamera nedostupná) za ' + delaySeconds + ' sekúnd...');
            console.warn('⛔ DEBUG: Selector:', videoSelector);

            const isVideoReady = () => {
                // Dynamicky hľadaj element pri každej kontrole
                const videoElement = typeof videoSelector === 'string'
                    ? document.querySelector(videoSelector)
                    : videoSelector;
                    
                if (!videoElement) {
                    console.log('🔍 DEBUG: videoElement neexistuje (selector:', videoSelector, ')');
                    return false;
                }
                if (!videoElement.srcObject) {
                    console.log('🔍 DEBUG: srcObject neexistuje');
                    return false;
                }
                const tracks = videoElement.srcObject.getVideoTracks();
                if (tracks.length === 0) {
                    console.log('🔍 DEBUG: žiadne video tracks');
                    return false;
                }
                if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                    console.log('🔍 DEBUG: videoWidth/Height je 0:', videoElement.videoWidth, videoElement.videoHeight);
                    return false;
                }
                console.log('🔍 DEBUG: Video je pripravený!');
                return videoElement;
            };
            
            const executeSimulation = () => {
                const videoElement = typeof videoSelector === 'string'
                    ? document.querySelector(videoSelector)
                    : videoSelector;
                    
                if (videoElement && videoElement.srcObject) {
                    const track = videoElement.srcObject.getVideoTracks()[0];
                    if (track) {
                        console.warn('⛔ DEBUG: Pred stop():', {
                            enabled: track.enabled,
                            muted: track.muted,
                            readyState: track.readyState
                        });
                        
                        // track.stop() ukončí stream a nastaví readyState='ended'
                        track.stop();
                        
                        console.warn('⛔ DEBUG: Po stop():', {
                            enabled: track.enabled,
                            muted: track.muted,
                            readyState: track.readyState
                        });
                        console.warn('⛔ DEBUG: Stream ukončený (readyState=ended)! Monitoring by mal reagovať okamžite');
                    }
                } else {
                    console.error('👾 DEBUG: Video element nemá srcObject');
                }
            };
            
            // Nastavíme MutationObserver
            console.log('⏳ DEBUG: Nastavujem MutationObserver na document.body...');
            
            const observer = new MutationObserver((mutations) => {
                console.log('🔔 DEBUG: MutationObserver zachytil zmenu v DOM, kontrolujem video...');
                const videoElement = isVideoReady();
                if (videoElement) {
                    observer.disconnect();
                    console.log('✅ DEBUG: Video pripravený, spúšťam simuláciu...');
                    setTimeout(executeSimulation, delaySeconds * 1000);
                }
            });
            
            // Sleduj document.body, lebo video element ešte neexistuje
            observer.observe(document.body, {
                attributes: true,
                subtree: true,
                childList: true
            });
            
            // Kontrola aktuálneho stavu
            console.log('🔍 DEBUG: Počiatočná kontrola stavu videa...');
            const videoElement = isVideoReady();
            if (videoElement) {
                observer.disconnect();
                console.log('✅ DEBUG: Video element už je pripravený!');
                setTimeout(executeSimulation, delaySeconds * 1000);
            } else {
                console.log('⏳ DEBUG: Video ešte nie je pripravené, čakám na MutationObserver...');
            }
        };
        
        /**
         * Zobrazí informácie o MediaStreamTrack stave v konzole
         * Užitočné pre debugging camera health check funkcií
         * 
         * @param {HTMLVideoElement} videoElement - Video element s kamerovým streamom
         * 
         * Zobrazuje:
         *   - Počet trackov (video a audio)
         *   - Pre každý track: kind, label, enabled, readyState, muted
         *   - enabled: či track renderuje stream (false = vypnutý "standard mute")
         *   - muted: či track nemá dáta kvôli technickému problému (obraz zamrzol)
         *   - readyState: 'live' alebo 'ended'
         * 
         * Podľa MDN:
         *   - enabled = false: "implement standard mute functionality"
         *   - muted = true: "no media because of a technical issue"
         */
        self.logTrackState = function(videoElement) {
            if (!videoElement || !videoElement.srcObject) {
                console.warn('👾 DEBUG: Video element alebo srcObject neexistuje');
                return;
            }
            
            const stream = videoElement.srcObject;
            const videoTracks = stream.getVideoTracks();
            const audioTracks = stream.getAudioTracks();
            
            console.log('📊 MediaStream Track State:');
            console.log('  Video tracks:', videoTracks.length);
            videoTracks.forEach((track, index) => {
                console.log('    [' + index + ']', track.kind, '|', track.label);
                console.log('        enabled:', track.enabled, '(false = vypnutý, nereenderuje stream)');
                console.log('        readyState:', track.readyState, '(live/ended)');
                console.log('        muted:', track.muted, '(true = TECHNICKÝ PROBLÉM - kamera zamrzla, obraz zostal)');
                
                // Interpretácia stavu
                if (!track.enabled) {
                    console.log('        ⚠️  Track je DISABLED - standard mute functionality');
                } else if (track.muted) {
                    console.log('        ⚠️  Track je MUTED - technický problém hardvéru (obraz zamrzol)');
                } else {
                    console.log('        ✅  Track funguje normálne');
                }
            });
            
            console.log('  Audio tracks:', audioTracks.length);
            audioTracks.forEach((track, index) => {
                console.log('    [' + index + ']', track.kind, '|', track.label);
                console.log('        enabled:', track.enabled);
                console.log('        readyState:', track.readyState);
                console.log('        muted:', track.muted);
            });
        };

    });
